import React, { createContext, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useLocationSearchSuggestions } from '@/hooks/useLocationSearchSuggestions';
import { usePopularDestinations } from '@/hooks/usePopularDestinations';
import { useRecentSearchesWithFilters } from '@/hooks/useRecentSearchesWithFilters';
import { setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import {
  getHeaderSearchAddressFilter,
  getHeaderSearchFilters,
} from '@/redux/selectors/globalHeader';

import { EHomeSearchWidgetVariant } from './HomeSearchWidget';
import { useNavigateToRecentSearch } from './recentSearch';

export interface ISuggestion {
  label: string;
  value?: unknown;
  url?: string;
  sublabel?: string;
}

type TLocationFilterContext = {
  addressValue: string;
  setAddressValue: (value: string) => void;
  suggestions: ISuggestion[];
  recentSearches: ISuggestion[];
  popularDestinations: ISuggestion[];
  navigateToRecentSearch: (url: string) => void;
  setMapboxValues: (mapboxId: string, fullMapboxAddress: string) => void;
};

const LocationFilterContext = createContext<TLocationFilterContext>({
  addressValue: '',
  setAddressValue: () => undefined,
  suggestions: [],
  recentSearches: [],
  popularDestinations: [],
  navigateToRecentSearch: () => undefined,
  setMapboxValues: () => undefined,
});

export const useLocationFilterContext = () => useContext(LocationFilterContext);

interface TLocationFilterProviderProps {
  onNavigateToRecentSearch?: () => void;
  variant?: EHomeSearchWidgetVariant;
}

export const LocationFilterProvider: React.FC<
  React.PropsWithChildren<TLocationFilterProviderProps>
> = ({ onNavigateToRecentSearch, variant, children }) => {
  const dispatch = useDispatch();
  const addressValue = useSelector(getHeaderSearchAddressFilter) || '';
  const { manual_address_input } = useSelector(getHeaderSearchFilters);

  useEffect(() => {
    if (variant === EHomeSearchWidgetVariant.RvDelivery && addressValue && !manual_address_input) {
      dispatch(
        setHeaderSearchFilter({
          address: '',
          manual_address_input: false,
        }),
      );
    }
  }, [variant, addressValue, manual_address_input, dispatch]);

  const setAddressValue = (value: string) => {
    dispatch(setHeaderSearchFilter({ address: value, manual_address_input: true }));
  };

  const setMapboxValues = (mapboxId: string, fullMapboxAddress: string) => {
    dispatch(
      setHeaderSearchFilter({ mapbox_id: mapboxId, full_mapbox_address: fullMapboxAddress }),
    );
  };

  const suggestions = useLocationSearchSuggestions(addressValue);
  const [recentSearches] = useRecentSearchesWithFilters();
  const popularDestinations = usePopularDestinations();

  const navigateToRecentSearch = useNavigateToRecentSearch(onNavigateToRecentSearch);

  return (
    <LocationFilterContext.Provider
      value={{
        addressValue,
        setAddressValue,
        suggestions,
        recentSearches,
        popularDestinations,
        navigateToRecentSearch,
        setMapboxValues,
      }}>
      {children}
    </LocationFilterContext.Provider>
  );
};
